<template>
  <div class="stock-summary-container">
    <!-- Current Department Warehouses (shown prominently) -->
    <div v-if="currentDeptItems.length > 0" class="current-dept-section">
      <span
        v-for="(item, index) in currentDeptItems"
        :key="'current-' + index"
        class="stock-tag current-dept"
      >
        <span class="warehouse-label">{{ item.warehouse }}:</span>
        <span class="stock-value">{{ item.stock || 0 }}</span>
        <span v-if="showPrices" class="price-value"
          >${{ item[priceLevel] || 0 }}</span
        >
      </span>
    </div>

    <!-- Other Departments Summary -->
    <div v-if="hasOtherDepts" class="other-depts-section">
      <button
        v-if="!expanded"
        @click.stop="expanded = true"
        class="other-depts-summary"
      >
        <span class="summary-label">Other Depts:</span>
        <span class="summary-stock">{{ otherDeptsTotal.stock }}</span>
        <i class="mdi mdi-chevron-down text-xs ml-1"></i>
      </button>

      <!-- Expanded view of other departments -->
      <div v-else class="other-depts-expanded">
        <button @click.stop="expanded = false" class="collapse-btn">
          <span>Other Departments</span>
          <i class="mdi mdi-chevron-up"></i>
        </button>
        <div class="other-depts-list">
          <div
            v-for="(dept, deptCode) in otherDeptsByDepartment"
            :key="deptCode"
            class="dept-group"
          >
            <span class="dept-code">{{ deptCode }}</span>
            <span class="dept-stock">{{ dept.totalStock }}</span>
            <span v-if="showPrices && dept.minPrice" class="dept-price"
              >from ${{ dept.minPrice }}</span
            >
          </div>
        </div>
      </div>
    </div>

    <!-- No stock message -->
    <span
      v-if="currentDeptItems.length === 0 && !hasOtherDepts"
      class="no-stock"
    >
      No stock info
    </span>
  </div>
</template>

<script>
export default {
  name: "StockSummaryTags",
  props: {
    pricesAndStock: {
      type: Array,
      required: true,
      default: () => []
    },
    currentDepartmentCode: {
      type: String,
      required: true
    },
    priceLevel: {
      type: String,
      default: "online"
    },
    showPrices: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      expanded: false
    };
  },
  computed: {
    // Items matching the current department
    currentDeptItems() {
      if (!this.pricesAndStock) return [];
      return this.pricesAndStock.filter(
        item => item.departamentoCodigo === this.currentDepartmentCode
      );
    },
    // Items from other departments
    otherDeptItems() {
      if (!this.pricesAndStock) return [];
      return this.pricesAndStock.filter(
        item => item.departamentoCodigo !== this.currentDepartmentCode
      );
    },
    hasOtherDepts() {
      return this.otherDeptItems.length > 0;
    },
    // Aggregate totals for other departments
    otherDeptsTotal() {
      const total = {
        stock: 0,
        minPrice: null
      };
      this.otherDeptItems.forEach(item => {
        total.stock += Number(item.stock) || 0;
        const price = Number(item[this.priceLevel]);
        if (price && (total.minPrice === null || price < total.minPrice)) {
          total.minPrice = price;
        }
      });
      return total;
    },
    // Group other departments by department code
    otherDeptsByDepartment() {
      const grouped = {};
      this.otherDeptItems.forEach(item => {
        const deptCode = item.departamentoCodigo;
        if (!grouped[deptCode]) {
          grouped[deptCode] = {
            totalStock: 0,
            minPrice: null,
            warehouses: []
          };
        }
        grouped[deptCode].totalStock += Number(item.stock) || 0;
        const price = item[this.priceLevel];
        if (
          price &&
          (grouped[deptCode].minPrice === null ||
            price < grouped[deptCode].minPrice)
        ) {
          grouped[deptCode].minPrice = price;
        }
        grouped[deptCode].warehouses.push(item);
      });
      return grouped;
    }
  }
};
</script>

<style lang="postcss" scoped>
.stock-summary-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.current-dept-section {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.stock-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.9375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
}

.stock-tag.current-dept {
  background: #dcfce7;
  color: #166534;
  font-weight: 500;
}

.stock-tag .warehouse-label {
  opacity: 0.8;
}

.stock-tag .stock-value {
  font-weight: 600;
}

.stock-tag .price-value {
  display: none;
  margin-left: 0.375rem;
  padding-left: 0.375rem;
  border-left: 1px solid currentColor;
  opacity: 0.7;
}

/* Desktop: Show prices */
@media (min-width: 768px) {
  .stock-tag .price-value {
    display: inline;
  }
}

/* Other Departments Summary Button */
.other-depts-summary {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.9375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  background: #f3f4f6;
  color: #4b5563;
  border: 1px dashed #d1d5db;
  cursor: pointer;
  transition: all 0.15s ease;
}

.other-depts-summary:hover {
  background: #e5e7eb;
  border-color: #9ca3af;
}

.summary-label {
  opacity: 0.7;
}

.summary-stock {
  font-weight: 600;
}

/* Expanded Other Departments */
.other-depts-expanded {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
}

.collapse-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
}

.collapse-btn:hover {
  background: #e5e7eb;
}

.other-depts-list {
  padding: 0.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.dept-group {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  padding: 0.375rem 0.75rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
}

.dept-code {
  font-weight: 600;
  color: #374151;
}

.dept-stock {
  color: #059669;
  font-weight: 500;
}

.dept-price {
  color: #6b7280;
  font-size: 0.8125rem;
}

.no-stock {
  font-size: 0.9375rem;
  color: #9ca3af;
  font-style: italic;
}
</style>
